import { RefreshOutlined } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { styled as MuiStyled } from '@mui/material/styles';
import AryCountDownCounter from 'framework/base/components/AryCountDownCounter';
import AryLoadingIconButton from 'framework/base/components/AryLoadingIconButton';
import AryModal from 'framework/base/components/AryModal';
import {
  CHECK_WHATSAPP_LOGGIN_STATE,
  GENERATE_WHATSAPP_INITIALIZATION_QR_CODE
} from 'framework/base/constants/aryGraphqlSchemas';
import { useAryLazyQuery } from 'framework/base/hooks/useAryGraphql';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import { useStore } from 'react-redux';
import { TypeAnimation } from 'react-type-animation';
import {
  setWhatsappLoadingState,
  setWhatsappLoginState
} from 'redux/slices/systemSlice';
import { imageAddressPrefix } from 'spec';
import AryLoadingAnimationMotion from './AryLoadingAnimationMotion';

// ***************** styled components *************************

const BackgroundBox = MuiStyled(Box)`
  width: 200px;
  height: 200px;
  background-color: ${(props) => props.theme.wrapper.grey50.backgroundColor};
  margin: 0 auto 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InnerCircularBox = MuiStyled(Box)`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.wrapper.grey100.backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ***************** end styled components *************************

const WHATSAPP_INITIALIZATION_TIME_IN_SECONDS = 60;
const OVERLAY_LOGO_SRC = `${imageAddressPrefix}static/img/brands/Moneyx.svg`;
const GET_NEW_QR_CODE_COUNT_LIMIT = 3;

const AryWhatsappInitializationModal = ({
  open,
  handleClose
  // checkUserLoginStateRefetch
}) => {
  const reduxStore = useStore();
  const countDownRef = useRef(null);
  const [counter, setCounter] = useState(0);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const { t } = useFrameworkTranslation();

  const incrementCounter = () => setCounter((prevCount) => prevCount + 1);
  const resetTimer = () => countDownRef.current.resetTime();

  const [
    generateWhatsappQRCodeString,
    { data: qrCodeString, loading: generateWhatsappQRCodeStringLoading }
  ] = useAryLazyQuery({
    gqlConfig: GENERATE_WHATSAPP_INITIALIZATION_QR_CODE,
    options: {
      fetchPolicy: 'network-only'
    }
  });
  const [checkUserLoginState, { data: checkUserLoginStateData }] =
    useAryLazyQuery({
      gqlConfig: CHECK_WHATSAPP_LOGGIN_STATE
    });

  useEffect(() => {
    let intervalId;

    const checkLoginStateAndClearInterval = async () => {
      await checkUserLoginState();
      if (checkUserLoginStateData?.isValid === true) {
        clearInterval(intervalId);
        reduxStore.dispatch(setWhatsappLoginState(true));
        reduxStore.dispatch(setWhatsappLoadingState(false));
        // checkUserLoginStateRefetch();
        handleClose();
      }
    };

    if (qrCodeString) {
      intervalId = setInterval(checkLoginStateAndClearInterval, 1000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [qrCodeString, checkUserLoginStateData]);

  async function getNewWhatsappQRCodeString() {
    incrementCounter();
    const resp = await generateWhatsappQRCodeString();
    if (resp?.data) {
      resetTimer();
    }
  }

  async function handleClickRefreshButton() {
    setCounter(1);
    const resp = await generateWhatsappQRCodeString();
    if (resp?.data) {
      setShowRefreshButton(false);
      resetTimer();
    }
  }

  async function handleTimeOut() {
    if (counter < GET_NEW_QR_CODE_COUNT_LIMIT) {
      getNewWhatsappQRCodeString();
    } else {
      setShowRefreshButton(true);
    }
  }

  useEffect(() => {
    getNewWhatsappQRCodeString();
  }, []);

  return (
    <AryModal
      title={t('Activating WhatsApp')}
      open={open}
      handleClose={handleClose}
      // noCloseOnOutsideClicked
      // rowSpacing="32px"
      titleColor="#000"
      style={{
        backgroundColor: '#fff'
      }}
      width="420px"
    >
      <Grid item xs={12} textAlign="center">
        {showRefreshButton ? (
          <BackgroundBox>
            <InnerCircularBox>
              <Typography variant="h6Bold">{t('Expired')}</Typography>
            </InnerCircularBox>
          </BackgroundBox>
        ) : generateWhatsappQRCodeStringLoading ? (
          <>
            <Grid item xs={12} textAlign="center">
              <Typography variant="h6Bold" color="secondary">
                {t('WhitsappTitle')}
              </Typography>
            </Grid>
            <Grid
              sx={{
                width: '200px',
                height: '200px',
                margin: '20px auto 0'
              }}
            >
              <AryLoadingAnimationMotion />
            </Grid>
          </>
        ) : (
          <>
            {qrCodeString?.qrCode ? (
              <>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="h6Bold" color="secondary">
                    {t('ConnectionTitle')}
                  </Typography>
                </Grid>
                <QRCodeSVG
                  value={qrCodeString?.qrCode}
                  imageSettings={{
                    src: OVERLAY_LOGO_SRC,
                    excavate: true,
                    width: 32,
                    height: 30
                  }}
                  size={200}
                  style={{
                    marginTop: '15px'
                  }}
                />
              </>
            ) : (
              <Grid
                sx={{
                  width: '200px',
                  height: '200px',
                  margin: '20px auto 0'
                }}
              >
                <AryLoadingAnimationMotion />
              </Grid>
            )}
            {/* <Grid item xs={12} textAlign="center" sx={{ margin: '10px 0' }}>
                          <Typography variant="h7Bold" color="primary">
                {t('whatsappInitialization.autoProcess')}
              </Typography>
          </Grid> */}
          </>
        )}
      </Grid>
      <Grid
        item
        xs={12}
        height="40px"
        textAlign="center"
        pt="0 !important"
        mb="32px"
      >
        {showRefreshButton && (
          <AryLoadingIconButton
            loading={generateWhatsappQRCodeStringLoading}
            onClick={handleClickRefreshButton}
          >
            <RefreshOutlined color="primary" />
          </AryLoadingIconButton>
        )}
        {generateWhatsappQRCodeStringLoading && (
          <Grid item xs={12} textAlign="center" pt="0">
            <Typography variant="h6Bold" color="primary">
              <TypeAnimation
                sequence={[t('ScanQrTitle'), 1000, t('ScanQrAlert'), 1000]}
                wrapper="span"
                speed={50}
                // style={{ fontSize: '2em', display: 'inline-block' }}
                repeat={Infinity}
              />
            </Typography>
          </Grid>
        )}
        {!generateWhatsappQRCodeStringLoading && (
          <AryCountDownCounter
            style={{
              marginTop: '16px !important'
            }}
            ref={countDownRef}
            initialTimeInSeconds={WHATSAPP_INITIALIZATION_TIME_IN_SECONDS}
            onTimeOut={handleTimeOut}
            hidden={showRefreshButton}
          />
        )}
      </Grid>
    </AryModal>
  );
};

export default AryWhatsappInitializationModal;
